import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { dayCareApi } from '@/api/daycare';
import { appointmentApi } from '@/api/appointment';
import { ipdApi } from '@/api/ipd';
import { DayCareStatus } from '@/types/daycare';
import type { DayCareCaseListItem, DayCareProcedure, DayCareResource } from '@/types/daycare';
import { VisitType } from '@/types/types';
import type { Appointment } from '@/types/types';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Calendar, Clock, User, CheckCircle2, ChevronRight, PlayCircle } from 'lucide-react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import {
  useCanMutateFrontDeskIpd,
  useFrontDeskBasePath,
} from '@/hooks/useFrontDeskBasePath';

const statusOrder: DayCareStatus[] = [
  DayCareStatus.SCHEDULED,
  DayCareStatus.CHECKED_IN,
  DayCareStatus.IN_PROCEDURE,
  DayCareStatus.RECOVERY,
  DayCareStatus.COMPLETED,
  DayCareStatus.DISCHARGED,
  DayCareStatus.CANCELLED,
];

function formatStatusLabel(status: DayCareStatus): string {
  return status.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
}

function formatTime(value: string) {
  if (!value) return '—';
  const d = new Date(value);
  return format(d, 'dd/MM/yyyy HH:mm');
}

export default function DayCareQueuePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const basePath = useFrontDeskBasePath();
  const canMutateIpd = useCanMutateFrontDeskIpd();
  const queryClient = useQueryClient();
  const [selectedDate, setSelectedDate] = useState(() =>
    format(new Date(), 'yyyy-MM-dd'),
  );
  const [statusFilter, setStatusFilter] = useState<DayCareStatus | undefined>();
  const [search, setSearch] = useState('');
  const [startDayCareAppointment, setStartDayCareAppointment] = useState<Appointment | null>(null);
  const [startProcedureId, setStartProcedureId] = useState('');
  const [startResourceId, setStartResourceId] = useState('');
  const [startNotes, setStartNotes] = useState('');

  // Admit directly from Day Care (without appointment)
  type PatientLite = {
    id: string;
    name: string;
    patientUniqueId?: string;
    uhid?: string;
    phone?: string;
    referralPersonName?: string | null;
  };
  type DoctorLite = { id: string; name: string; specialisation?: string | null };
  const SALES_DROPDOWN_STORAGE_KEY = 'thms:ipd:salesPersons';
  const DEFAULT_SALES_NAMES = ['Mohit', 'Nawaz', 'Tanish'];
  const PARTNER_DROPDOWN_STORAGE_KEY = 'thms:ipd:partnerLeads';
  const DEFAULT_PARTNER_NAMES = ['Surgenix', 'Carelan', 'Hexa', 'Saarthi', 'Mediedge'];
  const REFERRER_DROPDOWN_STORAGE_KEY = 'thms:ipd:referrers';
  const [admitOpen, setAdmitOpen] = useState(false);
  const [admitPatientQuery, setAdmitPatientQuery] = useState('');
  const [admitPatient, setAdmitPatient] = useState<PatientLite | null>(null);
  const [admitDoctorId, setAdmitDoctorId] = useState<string>('');
  const [admitProcedureId, setAdmitProcedureId] = useState<string>('');
  const [admitResourceId, setAdmitResourceId] = useState<string>('');
  const [admitScheduledAt, setAdmitScheduledAt] = useState<string>('');
  const [admitNotes, setAdmitNotes] = useState<string>('');
  const [admitSalesPersonName, setAdmitSalesPersonName] = useState('');
  const [admitPartnerLeadName, setAdmitPartnerLeadName] = useState('');
  const [admitReferralPersonName, setAdmitReferralPersonName] = useState('');
  const [salesOptions, setSalesOptions] = useState<string[]>(DEFAULT_SALES_NAMES);
  const [partnerOptions, setPartnerOptions] = useState<string[]>(DEFAULT_PARTNER_NAMES);
  const [customReferrerOptions, setCustomReferrerOptions] = useState<string[]>([]);
  const [addingSalesName, setAddingSalesName] = useState(false);
  const [newSalesName, setNewSalesName] = useState('');
  const [addingPartnerName, setAddingPartnerName] = useState(false);
  const [newPartnerName, setNewPartnerName] = useState('');
  const [addingReferrerName, setAddingReferrerName] = useState(false);
  const [newReferrerName, setNewReferrerName] = useState('');
  // Start-from-appointment attribution (same fields)
  const [startSalesPersonName, setStartSalesPersonName] = useState('');
  const [startPartnerLeadName, setStartPartnerLeadName] = useState('');
  const [startReferralPersonName, setStartReferralPersonName] = useState('');

  useEffect(() => {
    if (!admitOpen && !startDayCareAppointment) return;
    try {
      const raw = localStorage.getItem(SALES_DROPDOWN_STORAGE_KEY);
      const extra: string[] = raw ? JSON.parse(raw) : [];
      setSalesOptions(Array.from(new Set([...DEFAULT_SALES_NAMES, ...extra.filter(Boolean)])));
    } catch {
      setSalesOptions(DEFAULT_SALES_NAMES);
    }
    try {
      const raw = localStorage.getItem(PARTNER_DROPDOWN_STORAGE_KEY);
      const extra: string[] = raw ? JSON.parse(raw) : [];
      setPartnerOptions(Array.from(new Set([...DEFAULT_PARTNER_NAMES, ...extra.filter(Boolean)])));
    } catch {
      setPartnerOptions(DEFAULT_PARTNER_NAMES);
    }
    try {
      const raw = localStorage.getItem(REFERRER_DROPDOWN_STORAGE_KEY);
      const extra: string[] = raw ? JSON.parse(raw) : [];
      setCustomReferrerOptions(extra.filter(Boolean));
    } catch {
      setCustomReferrerOptions([]);
    }
  }, [admitOpen, startDayCareAppointment]);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['daycare-cases', selectedDate, statusFilter],
    queryFn: async () => {
      const res = await dayCareApi.getCases({
        date: selectedDate,
        status: statusFilter,
      });
      return res.data?.data ?? [];
    },
  });

  const { data: appointmentsData } = useQuery({
    queryKey: ['appointments-daycare', 'DAY_CARE'],
    queryFn: async () => {
      const res = await appointmentApi.getAppointmentsByHospitalAndVisitType(VisitType.DAY_CARE);
      return (res.data?.data ?? res.data) as Appointment[];
    },
  });

  const { data: patientsData, isLoading: patientsLoading } = useQuery({
    queryKey: ['patients-lite-daycare'],
    queryFn: async () => {
      const res = await ipdApi.getPatients();
      return (res.data?.data ?? []) as PatientLite[];
    },
    enabled: admitOpen,
  });

  const { data: doctorsData } = useQuery({
    queryKey: ['doctors-lite-daycare'],
    queryFn: async () => {
      const res = await ipdApi.getDoctors();
      return (res.data?.data ?? []) as DoctorLite[];
    },
    enabled: admitOpen || !!startDayCareAppointment,
  });

  const { data: proceduresData, isLoading: proceduresLoading } = useQuery({
    queryKey: ['daycare-procedures'],
    queryFn: async () => {
      const res = await dayCareApi.listProcedures();
      const raw = res.data as { data?: DayCareProcedure[] } | DayCareProcedure[] | undefined;
      return Array.isArray(raw) ? raw : (raw?.data ?? []);
    },
    enabled: !!startDayCareAppointment || admitOpen,
  });
  const { data: resourcesData } = useQuery({
    queryKey: ['daycare-resources'],
    queryFn: async () => {
      const res = await dayCareApi.listResources();
      const raw = res.data as { data?: DayCareResource[] } | DayCareResource[] | undefined;
      return Array.isArray(raw) ? raw : (raw?.data ?? []);
    },
    enabled: !!startDayCareAppointment || admitOpen,
  });

  const dayCareAppointmentsRaw = Array.isArray(appointmentsData) ? appointmentsData : [];
  const linkedAppointmentIds = useMemo(
    () => new Set((data ?? []).map((c) => (c as { appointmentId?: string | null }).appointmentId).filter(Boolean)),
    [data],
  );
  const dayCareAppointments = useMemo(() => {
    const d = selectedDate;
    return dayCareAppointmentsRaw.filter((apt) => {
      const aptDate = format(new Date(apt.scheduledAt), 'yyyy-MM-dd');
      return aptDate === d && !linkedAppointmentIds.has(apt.id);
    });
  }, [dayCareAppointmentsRaw, selectedDate, linkedAppointmentIds]);

  const procedures: DayCareProcedure[] = proceduresData ?? [];
  const resources: DayCareResource[] = resourcesData ?? [];
  const patients: PatientLite[] = patientsData ?? [];
  const doctors: DoctorLite[] = doctorsData ?? [];

  const referrerOptions = useMemo(() => {
    const doctorNames = doctors.map((d) => d.name).filter(Boolean);
    const merged = Array.from(new Set([...doctorNames, ...customReferrerOptions]));
    return [
      ...merged.map((n) => ({ value: n, label: n, searchText: n })),
      { value: '__add__', label: '+ Add referrer…', searchText: 'add referrer' },
    ];
  }, [doctors, customReferrerOptions]);

  const filteredPatients = useMemo(() => {
    const q = admitPatientQuery.trim().toLowerCase();
    if (!q) return [];
    return patients
      .filter((p) => {
        const name = (p.name ?? '').toLowerCase();
        const uhid = (p.uhid ?? p.patientUniqueId ?? '').toLowerCase();
        const phone = (p.phone ?? '').toLowerCase();
        return name.includes(q) || uhid.includes(q) || phone.includes(q);
      })
      .slice(0, 50);
  }, [patients, admitPatientQuery]);

  const createCaseFromAppointmentMutation = useMutation({
    mutationFn: (payload: {
      patientId: string;
      doctorId: string;
      procedureId: string;
      scheduledAt: string;
      appointmentId: string;
      resourceId?: string;
      notes?: string;
      salesPersonName?: string;
      partnerLeadName?: string;
      referralPersonName?: string;
    }) => dayCareApi.createCase(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['daycare-cases'] });
      queryClient.invalidateQueries({ queryKey: ['appointments-daycare'] });
      toast.success('Day Care case started');
      setStartDayCareAppointment(null);
      setStartProcedureId('');
      setStartResourceId('');
      setStartNotes('');
      setStartSalesPersonName('');
      setStartPartnerLeadName('');
      setStartReferralPersonName('');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to start Day Care case');
    },
  });

  const createCaseDirectMutation = useMutation({
    mutationFn: (payload: {
      patientId: string;
      doctorId?: string;
      procedureId: string;
      scheduledAt?: string;
      resourceId?: string;
      notes?: string;
      salesPersonName?: string;
      partnerLeadName?: string;
      referralPersonName?: string;
    }) => dayCareApi.createCase(payload),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['daycare-cases'] });
      toast.success('Day Care case created');
      const created = (res as any)?.data?.data as { id?: string } | undefined;
      setAdmitOpen(false);
      setAdmitPatientQuery('');
      setAdmitPatient(null);
      setAdmitDoctorId('');
      setAdmitProcedureId('');
      setAdmitResourceId('');
      setAdmitScheduledAt('');
      setAdmitNotes('');
      setAdmitSalesPersonName('');
      setAdmitPartnerLeadName('');
      setAdmitReferralPersonName('');
      if (created?.id) {
        navigate(`${basePath}/daycare/${created.id}`);
      }
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to admit to Day Care');
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, toStatus }: { id: string; toStatus: DayCareStatus }) =>
      dayCareApi.updateStatus(id, toStatus),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['daycare-cases'] });
      toast.success('Day Care status updated');
    },
    onError: (err: any) => {
      console.error('Failed to update Day Care status', err);
      toast.error(err?.response?.data?.message || 'Failed to update status');
    },
  });

  const cases: DayCareCaseListItem[] = data || [];

  const filteredCases = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return cases;
    return cases.filter((c) => {
      const name = (c.patient?.name ?? '').toLowerCase();
      const uhid = (c.patient?.uhid ?? '').toLowerCase();
      const phone = (c.patient?.phone ?? '').toLowerCase();
      const proc = (c.procedure?.name ?? '').toLowerCase();
      return (
        name.includes(q) ||
        uhid.includes(q) ||
        phone.includes(q) ||
        proc.includes(q)
      );
    });
  }, [cases, search]);

  const totalToday = cases.length;
  const inProgress = cases.filter((c) =>
    ['CHECKED_IN', 'IN_PROCEDURE', 'RECOVERY'].includes(c.status),
  ).length;
  const completed = cases.filter((c) => c.status === 'DISCHARGED').length;

  const nextStatuses: Partial<Record<DayCareStatus, DayCareStatus>> = {
    [DayCareStatus.SCHEDULED]: DayCareStatus.CHECKED_IN,
    [DayCareStatus.CHECKED_IN]: DayCareStatus.IN_PROCEDURE,
    [DayCareStatus.IN_PROCEDURE]: DayCareStatus.RECOVERY,
    [DayCareStatus.RECOVERY]: DayCareStatus.COMPLETED,
    [DayCareStatus.COMPLETED]: DayCareStatus.DISCHARGED,
  };

  useEffect(() => {
    // initial fetch is handled by react-query; this keeps placeholder consistent
  }, []);

  return (
    <div className="thms-page-shell">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-artis-serif text-2xl font-semibold text-artis-brown-dark">Day Care Queue</h1>
          <p className="thms-page-subtitle">
            Manage same-day treatment cases (observation, procedures, dialysis, etc.)
          </p>
          {!canMutateIpd && user?.role === 'OPERATIONS_MANAGER' ? (
            <p className="text-xs text-slate-600 mt-1">
              View only — you can open cases and bills; admitting or advancing status requires reception.
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {canMutateIpd && (
            <Button
              size="sm"
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => {
                const now = new Date();
                const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
                  .toISOString()
                  .slice(0, 16);
                setAdmitScheduledAt(local);
                setAdmitOpen(true);
              }}
            >
              Admit patient
            </Button>
          )}
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Date</label>
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-[160px]"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Status</label>
            <select
              className="border rounded-md px-2 py-1 text-sm"
              value={statusFilter || ''}
              onChange={(e) =>
                setStatusFilter(
                  e.target.value ? (e.target.value as DayCareStatus) : undefined,
                )
              }
            >
              <option value="">All</option>
              {statusOrder.map((s) => (
                <option key={s} value={s}>
                  {formatStatusLabel(s)}
                </option>
              ))}
            </select>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">
              Today&apos;s Cases
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-blue-600">{totalToday}</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">
              In Progress
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-amber-600">{inProgress}</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">
              Discharged Today
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-emerald-600">{completed}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {dayCareAppointments.length > 0 && (
        <Card className="shadow-sm border-amber-100 bg-amber-50/30">
          <CardHeader className="flex flex-row items-center justify-between gap-4">
            <CardTitle className="flex items-center gap-2 text-amber-800">
              <PlayCircle className="h-5 w-5" />
              Day Care Appointments (start a case to add to queue)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg border border-amber-200 bg-white">
              <Table>
                <TableHeader>
                  <TableRow className="bg-amber-50/50">
                    <TableHead>Time</TableHead>
                    <TableHead>Patient</TableHead>
                    <TableHead>Doctor</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dayCareAppointments.map((apt) => (
                    <TableRow key={apt.id} className="hover:bg-amber-50/40">
                      <TableCell className="whitespace-nowrap text-sm text-gray-700">
                        {formatTime(apt.scheduledAt instanceof Date ? apt.scheduledAt.toISOString() : String(apt.scheduledAt ?? ''))}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium text-gray-900">
                            {(apt as Appointment).patient?.name ?? '—'}
                          </span>
                          <span className="text-xs text-gray-500">
                            UHID: {(apt as Appointment).patient?.uhid ?? '—'} · {(apt as Appointment).patient?.phone ?? '—'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3 text-gray-500" />
                          <span className="text-sm text-gray-800">
                            {(apt as Appointment).doctor?.name ?? '—'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="default"
                          size="sm"
                          className="bg-amber-600 hover:bg-amber-700"
                          onClick={() => {
                            setStartDayCareAppointment(apt);
                            setStartReferralPersonName(
                              (apt.patient as { referralPersonName?: string | null } | undefined)
                                ?.referralPersonName?.trim() || ''
                            );
                            setStartSalesPersonName('');
                            setStartPartnerLeadName('');
                          }}
                        >
                          <PlayCircle className="h-3 w-3 mr-1" />
                          Start day care
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-blue-600" />
            Today&apos;s Day Care Cases
          </CardTitle>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Input
                placeholder="Search by patient, UHID, phone or procedure"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-[260px] pl-8"
              />
              <Clock className="h-4 w-4 text-gray-400 absolute left-2 top-2.5" />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50/50">
                  <TableHead>DC #</TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead>Patient</TableHead>
                  <TableHead>Procedure</TableHead>
                  <TableHead>Doctor</TableHead>
                  <TableHead>Resource</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="py-8 text-center text-gray-500">
                      Loading Day Care cases...
                    </TableCell>
                  </TableRow>
                ) : filteredCases.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="py-8 text-center text-gray-500">
                      No Day Care cases for the selected date.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCases.map((c) => {
                    const next = nextStatuses[c.status];
                    return (
                      <TableRow key={c.id} className="hover:bg-gray-50/60">
                        <TableCell className="whitespace-nowrap text-sm font-medium text-gray-900">
                          {c.dcNumber || c.id.slice(0, 8).toUpperCase()}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-sm text-gray-700">
                          {formatTime(c.scheduledAt)}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="font-medium text-gray-900">
                              {c.patient?.name}
                            </span>
                            <span className="text-xs text-gray-500">
                              UHID: {c.patient?.uhid || '—'} · {c.patient?.phone}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="font-medium text-gray-900">
                              {c.procedure?.name}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <User className="h-3 w-3 text-gray-500" />
                            <span className="text-sm text-gray-800">
                              {c.doctor?.name}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-gray-700">
                          {c.resource?.name || '—'}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className="text-xs capitalize border-blue-100 bg-blue-50 text-blue-700"
                          >
                            {formatStatusLabel(c.status)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`${basePath}/daycare/${c.id}`)}
                              title="View case"
                            >
                              <ChevronRight className="h-3 w-3 mr-1" />
                              View
                            </Button>
                            {next && user?.role === 'RECEPTIONIST' && (
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() =>
                                  updateStatusMutation.mutate({
                                    id: c.id,
                                    toStatus: next,
                                  })
                                }
                              >
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                {formatStatusLabel(next)}
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!startDayCareAppointment} onOpenChange={(open) => !open && setStartDayCareAppointment(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Start Day Care Case</DialogTitle>
          </DialogHeader>
          {startDayCareAppointment && (
            <div className="space-y-4 pt-2">
              <p className="text-sm text-gray-600">
                Patient: <strong>{(startDayCareAppointment as Appointment).patient?.name}</strong>
                {' · '}
                Doctor: <strong>{(startDayCareAppointment as Appointment).doctor?.name}</strong>
              </p>
              <div className="space-y-2">
                <Label>Procedure (required)</Label>
                <Select value={startProcedureId} onValueChange={setStartProcedureId} required>
                  <SelectTrigger>
                    <SelectValue placeholder="Select procedure" />
                  </SelectTrigger>
                  <SelectContent className="z-[100]" position="popper">
                    {proceduresLoading ? (
                      <SelectItem value="__loading__" disabled>
                        Loading…
                      </SelectItem>
                    ) : procedures.length === 0 ? (
                      <SelectItem value="__no_procedures__" disabled>
                        No procedures configured
                      </SelectItem>
                    ) : (
                      procedures.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Resource (optional)</Label>
                <Select value={startResourceId} onValueChange={setStartResourceId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select resource" />
                  </SelectTrigger>
                  <SelectContent className="z-[100]" position="popper">
                    <SelectItem value="__none__">None</SelectItem>
                    {resources.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Input
                  value={startNotes}
                  onChange={(e) => setStartNotes(e.target.value)}
                  placeholder="Notes"
                  className="resize-none"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Referrer Name</Label>
                  <div className="flex items-center gap-2">
                    <SearchableSelect
                      value={startReferralPersonName}
                      onValueChange={(v) => {
                        if (v === '__add__') {
                          setAddingReferrerName(true);
                          return;
                        }
                        setStartReferralPersonName(v);
                      }}
                      options={referrerOptions}
                      placeholder="Select referrer (optional)"
                      searchPlaceholder="Type doctor name…"
                      contentClassName="z-[100]"
                      emptyMessage="No doctors match your search."
                    />
                    {startReferralPersonName ? (
                      <Button type="button" variant="outline" onClick={() => setStartReferralPersonName('')}>
                        Clear
                      </Button>
                    ) : null}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Sales Person</Label>
                  <div className="flex items-center gap-2">
                    <Select
                      value={startSalesPersonName}
                      onValueChange={(v) => {
                        if (v === '__add__') {
                          setAddingSalesName(true);
                          return;
                        }
                        setStartSalesPersonName(v);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select sales person (optional)" />
                      </SelectTrigger>
                      <SelectContent className="z-[100]">
                        {salesOptions.map((n) => (
                          <SelectItem key={n} value={n}>
                            {n}
                          </SelectItem>
                        ))}
                        <SelectItem value="__add__">+ Add name…</SelectItem>
                      </SelectContent>
                    </Select>
                    {startSalesPersonName ? (
                      <Button type="button" variant="outline" onClick={() => setStartSalesPersonName('')}>
                        Clear
                      </Button>
                    ) : null}
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Partner lead</Label>
                <div className="flex items-center gap-2">
                  <Select
                    value={startPartnerLeadName}
                    onValueChange={(v) => {
                      if (v === '__add__') {
                        setAddingPartnerName(true);
                        return;
                      }
                      setStartPartnerLeadName(v);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select partner lead (optional)" />
                    </SelectTrigger>
                    <SelectContent className="z-[100]">
                      {partnerOptions.map((n) => (
                        <SelectItem key={n} value={n}>
                          {n}
                        </SelectItem>
                      ))}
                      <SelectItem value="__add__">+ Add name…</SelectItem>
                    </SelectContent>
                  </Select>
                  {startPartnerLeadName ? (
                    <Button type="button" variant="outline" onClick={() => setStartPartnerLeadName('')}>
                      Clear
                    </Button>
                  ) : null}
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setStartDayCareAppointment(null)}>
                  Cancel
                </Button>
                <Button
                  disabled={!startProcedureId || createCaseFromAppointmentMutation.isPending}
                  onClick={() => {
                    if (!startProcedureId || !startDayCareAppointment) return;
                    const apt = startDayCareAppointment as Appointment;
                    createCaseFromAppointmentMutation.mutate({
                      patientId: apt.patientId,
                      doctorId: apt.doctorId,
                      procedureId: startProcedureId,
                      scheduledAt: new Date(apt.scheduledAt).toISOString(),
                      appointmentId: apt.id,
                      resourceId: startResourceId && startResourceId !== '__none__' ? startResourceId : undefined,
                      notes: startNotes || undefined,
                      salesPersonName: startSalesPersonName || undefined,
                      partnerLeadName: startPartnerLeadName || undefined,
                      referralPersonName: startReferralPersonName || undefined,
                    });
                  }}
                >
                  {createCaseFromAppointmentMutation.isPending ? 'Creating...' : 'Create case'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={admitOpen} onOpenChange={setAdmitOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Admit patient to Day Care</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Patient (required)</Label>
              <Input
                placeholder="Search by name, UHID, phone…"
                value={admitPatientQuery}
                onChange={(e) => setAdmitPatientQuery(e.target.value)}
              />
              {admitPatientQuery && (
                <div className="rounded-md border bg-white shadow-sm max-h-48 overflow-y-auto">
                  {patientsLoading ? (
                    <div className="p-2 text-sm text-artis-slate">Loading patients…</div>
                  ) : filteredPatients.length === 0 ? (
                    <div className="p-2 text-sm text-artis-slate">No matches</div>
                  ) : (
                    filteredPatients.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        className="w-full text-left cursor-pointer p-2 hover:bg-gray-100 transition"
                        onClick={() => {
                          setAdmitPatient(p);
                          setAdmitPatientQuery(p.name);
                          setAdmitReferralPersonName(p.referralPersonName?.trim() || '');
                        }}
                      >
                        <div className="font-medium">{p.name}</div>
                        <div className="text-xs text-gray-500">
                          {(p.uhid ?? p.patientUniqueId ?? '—')} · {p.phone ?? '—'}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
              {admitPatient && (
                <p className="text-sm text-emerald-700">
                  Selected: <strong>{admitPatient.name}</strong>
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Procedure (required)</Label>
                <Select value={admitProcedureId} onValueChange={setAdmitProcedureId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select procedure" />
                  </SelectTrigger>
                  <SelectContent className="z-[100]" position="popper">
                    {proceduresLoading ? (
                      <SelectItem value="__loading__" disabled>
                        Loading…
                      </SelectItem>
                    ) : procedures.length === 0 ? (
                      <SelectItem value="__no_procedures__" disabled>
                        No procedures configured
                      </SelectItem>
                    ) : (
                      procedures.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Doctor (optional)</Label>
                <SearchableSelect
                  value={admitDoctorId}
                  onValueChange={setAdmitDoctorId}
                  placeholder="Select doctor"
                  searchPlaceholder="Type doctor name…"
                  contentClassName="z-[100]"
                  options={[
                    { value: '__none__', label: 'Auto' },
                    ...doctors.map((d) => ({
                      value: d.id,
                      label: d.name,
                      searchText: d.name,
                    })),
                  ]}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Resource (optional)</Label>
                <Select value={admitResourceId} onValueChange={setAdmitResourceId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select resource" />
                  </SelectTrigger>
                  <SelectContent className="z-[100]" position="popper">
                    <SelectItem value="__none__">None</SelectItem>
                    {resources.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Scheduled at</Label>
                <Input
                  type="datetime-local"
                  value={admitScheduledAt}
                  onChange={(e) => setAdmitScheduledAt(e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Referrer Name</Label>
                <div className="flex items-center gap-2">
                  <SearchableSelect
                    value={admitReferralPersonName}
                    onValueChange={(v) => {
                      if (v === '__add__') {
                        setAddingReferrerName(true);
                        return;
                      }
                      setAdmitReferralPersonName(v);
                    }}
                    options={referrerOptions}
                    placeholder="Select referrer (optional)"
                    searchPlaceholder="Type doctor name…"
                    contentClassName="z-[100]"
                    emptyMessage="No doctors match your search."
                  />
                  {admitReferralPersonName ? (
                    <Button type="button" variant="outline" onClick={() => setAdmitReferralPersonName('')}>
                      Clear
                    </Button>
                  ) : null}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Sales Person</Label>
                <div className="flex items-center gap-2">
                  <Select
                    value={admitSalesPersonName}
                    onValueChange={(v) => {
                      if (v === '__add__') {
                        setAddingSalesName(true);
                        return;
                      }
                      setAdmitSalesPersonName(v);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select sales person (optional)" />
                    </SelectTrigger>
                    <SelectContent className="z-[100]">
                      {salesOptions.map((n) => (
                        <SelectItem key={n} value={n}>
                          {n}
                        </SelectItem>
                      ))}
                      <SelectItem value="__add__">+ Add name…</SelectItem>
                    </SelectContent>
                  </Select>
                  {admitSalesPersonName ? (
                    <Button type="button" variant="outline" onClick={() => setAdmitSalesPersonName('')}>
                      Clear
                    </Button>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Partner lead</Label>
              <div className="flex items-center gap-2">
                <Select
                  value={admitPartnerLeadName}
                  onValueChange={(v) => {
                    if (v === '__add__') {
                      setAddingPartnerName(true);
                      return;
                    }
                    setAdmitPartnerLeadName(v);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select partner lead (optional)" />
                  </SelectTrigger>
                  <SelectContent className="z-[100]">
                    {partnerOptions.map((n) => (
                      <SelectItem key={n} value={n}>
                        {n}
                      </SelectItem>
                    ))}
                    <SelectItem value="__add__">+ Add name…</SelectItem>
                  </SelectContent>
                </Select>
                {admitPartnerLeadName ? (
                  <Button type="button" variant="outline" onClick={() => setAdmitPartnerLeadName('')}>
                    Clear
                  </Button>
                ) : null}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Input value={admitNotes} onChange={(e) => setAdmitNotes(e.target.value)} placeholder="Notes" />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAdmitOpen(false)}>
                Cancel
              </Button>
              <Button
                disabled={!admitPatient?.id || !admitProcedureId || createCaseDirectMutation.isPending}
                onClick={() => {
                  if (!admitPatient?.id || !admitProcedureId) return;
                  const scheduledIso = admitScheduledAt
                    ? new Date(admitScheduledAt).toISOString()
                    : new Date().toISOString();
                  createCaseDirectMutation.mutate({
                    patientId: admitPatient.id,
                    doctorId: admitDoctorId && admitDoctorId !== '__none__' ? admitDoctorId : undefined,
                    procedureId: admitProcedureId,
                    scheduledAt: scheduledIso,
                    resourceId: admitResourceId && admitResourceId !== '__none__' ? admitResourceId : undefined,
                    notes: admitNotes.trim() ? admitNotes.trim() : undefined,
                    salesPersonName: admitSalesPersonName || undefined,
                    partnerLeadName: admitPartnerLeadName || undefined,
                    referralPersonName: admitReferralPersonName || undefined,
                  });
                }}
              >
                {createCaseDirectMutation.isPending ? 'Creating…' : 'Create case'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={addingSalesName} onOpenChange={setAddingSalesName}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Add sales person</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <Input
              value={newSalesName}
              onChange={(e) => setNewSalesName(e.target.value)}
              placeholder="Enter name"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAddingSalesName(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  const nm = newSalesName.trim();
                  if (!nm) return;
                  const next = Array.from(new Set([...salesOptions, nm]));
                  setSalesOptions(next);
                  const extra = next.filter((x) => !DEFAULT_SALES_NAMES.includes(x));
                  localStorage.setItem(SALES_DROPDOWN_STORAGE_KEY, JSON.stringify(extra));
                  if (admitOpen) setAdmitSalesPersonName(nm);
                  else setStartSalesPersonName(nm);
                  setNewSalesName('');
                  setAddingSalesName(false);
                }}
              >
                Add
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={addingPartnerName} onOpenChange={setAddingPartnerName}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Add partner lead</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <Input
              value={newPartnerName}
              onChange={(e) => setNewPartnerName(e.target.value)}
              placeholder="Enter name"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAddingPartnerName(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  const nm = newPartnerName.trim();
                  if (!nm) return;
                  const next = Array.from(new Set([...partnerOptions, nm]));
                  setPartnerOptions(next);
                  const extra = next.filter((x) => !DEFAULT_PARTNER_NAMES.includes(x));
                  localStorage.setItem(PARTNER_DROPDOWN_STORAGE_KEY, JSON.stringify(extra));
                  if (admitOpen) setAdmitPartnerLeadName(nm);
                  else setStartPartnerLeadName(nm);
                  setNewPartnerName('');
                  setAddingPartnerName(false);
                }}
              >
                Add
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={addingReferrerName} onOpenChange={setAddingReferrerName}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Add referrer</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <Input
              value={newReferrerName}
              onChange={(e) => setNewReferrerName(e.target.value)}
              placeholder="Enter referrer name"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAddingReferrerName(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  const nm = newReferrerName.trim();
                  if (!nm) return;
                  const next = Array.from(new Set([...customReferrerOptions, nm]));
                  setCustomReferrerOptions(next);
                  localStorage.setItem(REFERRER_DROPDOWN_STORAGE_KEY, JSON.stringify(next));
                  if (admitOpen) setAdmitReferralPersonName(nm);
                  else setStartReferralPersonName(nm);
                  setNewReferrerName('');
                  setAddingReferrerName(false);
                }}
              >
                Add
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

